import React, { useState, useEffect, useCallback } from 'react';
import Project from '../../common/Utils/project';
import { useAuth } from 'oidc-react';
import Pagination from '../../components/Pagination/Pagination';
import { useNavigate } from 'react-router-dom';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api from '../../components/services/apiService';
import { useDebounce } from '../../hooks/useDebounce';
import moment from 'moment';
import { getConditionalStatusBadgeClass, getStatusDisplayText } from '../../common/Utils/color';

interface DeviceInsight {
  id?: string;
  remediationPlanId: string;
  remediationPlanType: string;
  remediationPlanName: string;
  remediationPlanScheduledTime: string;
  remediationPlanStartTime: string;
  remediationPlanEndTime: string;
  agentRemediationPlanStatus: string;
  cpuUtilizationPercent: string;
  ramUsagePercent: string;
}

interface RemediationPlanAgent {
  remediationPlanAgentDetails: DeviceInsight[];
}

interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface DeviceInsightResponse {
  data: RemediationPlanAgent[];
  message: string;
  isSuccess: boolean;
  pagination: PaginationInfo;
}

interface Props {
  deviceData?: any;
}

const DeviceInsights: React.FC<Props> = ({ deviceData }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [deviceInsights, setDeviceInsights] = useState<DeviceInsight[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all'); // Add status filter state
  const debouncedSearch = useDebounce(searchTerm, 600);
  const itemsPerPage = 20;
  const { selectedTenantId } = useTenantSelection();

  // Extract unique statuses from deviceInsights for the filter dropdown
  const uniqueStatuses = Array.from(new Set(deviceInsights.map((item) => item.agentRemediationPlanStatus).filter(Boolean)));

  // Filter deviceInsights based on status
  const filteredInsights = deviceInsights.filter((item) => {
    if (statusFilter === 'all') return true;
    return item.agentRemediationPlanStatus === statusFilter;
  });

  // Frontend pagination over the filtered set
  const totalPages = Math.max(1, Math.ceil(filteredInsights.length / itemsPerPage));
  const pagedInsights = filteredInsights.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Fetches every matching record in one call (large pageSize) so pagination can be
  // done entirely on the client — keeps page counts correct once the status filter
  // (which only runs client-side) is applied on top of the fetched set.
  const fetchDeviceInsights = useCallback(
    async (search: string = '') => {
      setLoadingDevices(true);
      try {
        const token = auth.userData?.access_token;
        const tenantId = selectedTenantId;
        const agentId = deviceData?.agentId || localStorage.getItem('agent_id');

        if (!token || !tenantId || !agentId) return;

        const queryParams = new URLSearchParams({
          pageNumber: '1',
          pageSize: '9999',
        });
        if (search.trim()) queryParams.append('search', search.trim());

        const res = await api.get<DeviceInsightResponse>(
          `${Project.api}patchcenterservice/RemediationPlan/remediation-plan/${agentId}?${queryParams}`,
        );

        if (res.data.isSuccess) {
          const insights = res.data.data[0]?.remediationPlanAgentDetails ?? [];
          // Sort by remediationPlanScheduledTime in descending order (newest first)
          const sortedInsights = insights.sort((a, b) => {
            const dateA = new Date(a.remediationPlanScheduledTime).getTime();
            const dateB = new Date(b.remediationPlanScheduledTime).getTime();
            return dateB - dateA; // Descending order
          });
          setDeviceInsights(sortedInsights);
        }
      } catch (error) {
        console.error('Error fetching device insights:', error);
        setDeviceInsights([]);
      } finally {
        setLoadingDevices(false);
      }
    },
    [auth.userData, deviceData?.agentId],
  );

  const ViewRemediationPlanTask = async (remediationPlanId: string) => {
    const agentId = deviceData?.agentId || localStorage.getItem('agent_id');
    navigate(`/patchcenter/${remediationPlanId}/DeviceInsights/${agentId}`);
  };

  useEffect(() => {
    fetchDeviceInsights(searchTerm);
    setCurrentPage(1);
  }, [fetchDeviceInsights, debouncedSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  // Loading skeleton
  const LoadingSkeleton = ({ columnCount }: any) => (
    <>
      {[...Array(itemsPerPage)].map((_, index) => (
        <tr
          key={`skeleton-${index}`}
          className={`${index % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'} animate-slideIn`}
          style={{ animationDelay: `${index * 100}ms` }}
        >
          {[...Array(columnCount)].map((_, idx) => (
            <td key={idx} className="p-2" style={{ height: '70px' }}>
              <div className="animate-pulse bg-zinc-700 h-4 rounded animate-shimmer"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <div className="p-4 bg-zinc-900 rounded-lg shadow-md">
      {/* Search and Filter */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <input
            type="text"
            placeholder="Search by plan name or activity"
            value={searchTerm}
            onChange={handleSearchChange}
            className="bg-zinc-800 text-white px-4 py-2 rounded w-full border border-zinc-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 pr-10 transition-all"
            aria-label="Search remediation plans"
          />
          {searchTerm && !loadingDevices && (
            <button
              onClick={() => {
                setSearchTerm('');
                fetchDeviceInsights('');
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="relative min-w-[180px]">
          <select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            className="bg-zinc-800 text-white px-4 py-2 rounded border border-zinc-700 focus:border-red-500 focus:ring-1 focus:ring-red-500 w-full transition-all"
            aria-label="Filter by status"
          >
            <option value="all">All Statuses</option>
            {uniqueStatuses.map((status) => (
              <option key={status} value={status}>
                {getStatusDisplayText(status)}
              </option>
            ))}
          </select>
        </div>

        {statusFilter !== 'all' && (
          <button
            onClick={() => setStatusFilter('all')}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors duration-200 font-semibold"
            aria-label="Clear status filter"
          >
            Clear Filter
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg">
        <table className="w-full text-sm table-auto border-collapse">
          <thead className="bg-zinc-800">
            <tr className="text-left">
              <th className="p-3">Type</th>
              <th className="p-3">Activity</th>
              <th className="p-3">Start Time</th>
              <th className="p-3">End Time</th>
              <th className="p-3">Status</th>
              <th className="p-3">CPU Usage</th>
              <th className="p-3">Memory Usage</th>
            </tr>
          </thead>
          <tbody>
            {loadingDevices ? (
              <LoadingSkeleton columnCount={7} />
            ) : filteredInsights.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-gray-400">
                  {statusFilter !== 'all' ? 'No device insights found for selected status' : 'No device insights found'}
                </td>
              </tr>
            ) : (
              pagedInsights.map((item, i) => (
                <tr
                  key={item.id || i}
                  className={`${i % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'} hover:bg-zinc-700 transition-all`}
                  style={{ height: '60px' }}
                >
                  <td
                    className="p-3 cursor-pointer underline hover:text-red-500 truncate max-w-[200px]"
                    onClick={() => ViewRemediationPlanTask(item.remediationPlanId)}
                    title={item.remediationPlanName}
                    tabIndex={0}
                    aria-label={`View remediation plan ${item.remediationPlanName}`}
                  >
                    {item.remediationPlanName}
                  </td>
                  <td className="p-3 truncate max-w-[120px]" title={item.remediationPlanType}>
                    {item.remediationPlanType}
                  </td>
                  <td className="p-3">
                    {item.remediationPlanStartTime && !item.remediationPlanStartTime.startsWith('0001-01-01')
                      ? moment(item.remediationPlanStartTime).format('DD MMM YYYY, hh:mm A')
                      : '-'}
                  </td>
                  <td className="p-3">
                    {item.remediationPlanEndTime && !item.remediationPlanEndTime.startsWith('0001-01-01')
                      ? moment(item.remediationPlanEndTime).format('DD MMM YYYY, hh:mm A')
                      : '-'}
                  </td>
                  <td className="p-3">
                    <span className={`${getConditionalStatusBadgeClass(item.agentRemediationPlanStatus)} px-2 py-1 rounded font-semibold`}>
                      {getStatusDisplayText(item.agentRemediationPlanStatus)}
                    </span>
                  </td>
                  <td className="p-3">
                    {item.cpuUtilizationPercent ? (
                      <span className="text-white px-2 py-1 rounded text-sm">{item.cpuUtilizationPercent}%</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="p-3">
                    {item.ramUsagePercent ? <span className="text-white px-2 py-1 rounded text-sm">{item.ramUsagePercent}%</span> : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} loading={loadingDevices} onPageChange={handlePageChange} />
    </div>
  );
};

export default DeviceInsights;
